import React from 'react';
import { ClockIcon, EditIcon, SaveIcon, CancelIcon } from '../../components/icons/Icons';
import { formatHours } from '../../utils/format';

interface MemberTimeEntry {
  userId: string;
  userName: string;
  hours: number;
}

interface TimeTrackingCardProps {
  estimatedHours: number;
  actualHours: number;
  hoursOverBudget: boolean;
  timeEntryCount: number;
  isProjectManager: boolean;
  editingHours: boolean;
  draftHours: string;
  onStartEditHours: () => void;
  onSaveHours: () => void;
  onCancelEditHours: () => void;
  onDraftHoursChange: (value: string) => void;
  timeBreakdown?: MemberTimeEntry[];
}

export const TimeTrackingCard: React.FC<TimeTrackingCardProps> = ({
  estimatedHours,
  actualHours,
  hoursOverBudget,
  timeEntryCount,
  isProjectManager,
  editingHours,
  draftHours,
  onStartEditHours,
  onSaveHours,
  onCancelEditHours,
  onDraftHoursChange,
  timeBreakdown,
}) => {
  return (
    <div className="detail-card">
      <h3 className="detail-card-title">
        <ClockIcon />
        Time Tracking
      </h3>
      <div className="detail-field">
        <span className="field-label">
          Estimated
          {isProjectManager && !editingHours && (
            <button className="action-icon-btn edit" onClick={onStartEditHours} title="Edit"><EditIcon /></button>
          )}
          {isProjectManager && editingHours && (
            <span className="action-btn-group">
              <button className="action-icon-btn save" onClick={onSaveHours} title="Save"><SaveIcon /></button>
              <button className="action-icon-btn cancel" onClick={onCancelEditHours} title="Cancel"><CancelIcon /></button>
            </span>
          )}
        </span>
        {editingHours ? (
          <span className="field-value field-edit-row">
            <input
              type="number"
              className="inline-edit-input"
              value={draftHours}
              onChange={e => onDraftHoursChange(e.target.value)}
              min="0"
              step="0.25"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') onSaveHours(); if (e.key === 'Escape') onCancelEditHours(); }}
            />
            <span className="inline-edit-hint">hours</span>
          </span>
        ) : (
          <span className="field-value">{formatHours(estimatedHours)}</span>
        )}
      </div>
      <div className="detail-field">
        <span className="field-label">Actual</span>
        <span className={`field-value tabular-nums ${hoursOverBudget ? 'text-danger' : 'text-success'}`}>
          {formatHours(actualHours)}
        </span>
      </div>
      <div className="detail-field">
        <span className="field-label">Remaining</span>
        <span className="field-value tabular-nums">
          {hoursOverBudget
            ? <span className="text-danger">Over by {formatHours(actualHours - estimatedHours)}</span>
            : formatHours(estimatedHours - actualHours)
          }
        </span>
      </div>
      {timeEntryCount > 0 && (
        <div className="detail-field">
          <span className="field-label">Time Entries</span>
          <span className="field-value">{timeEntryCount} segment{timeEntryCount !== 1 ? 's' : ''}</span>
        </div>
      )}
      {timeBreakdown && timeBreakdown.length > 0 && (
        <div className="detail-field time-breakdown">
          <span className="field-label">By Member</span>
          <div className="time-breakdown-list">
            {timeBreakdown.map(entry => (
              <div key={entry.userId} className="time-breakdown-row">
                <span className="time-breakdown-name">{entry.userName}</span>
                <span className="time-breakdown-hours">{formatHours(entry.hours)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
